import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GET_COMPANY, GetCompanyQuery, GetCompanyQueryVariables } from '../graphql';

export type BulkTicketingDataUpload = GetCompanyQuery['getCompany']['bulkTicketingDataUploads'][number];
export type BulkPriorYearTaxDocumentUpload = GetCompanyQuery['getCompany']['bulkPriorYearTaxDocumentUploads'][number];

export const useCompany = ({
  companyId,
}: {
  companyId: string | null;
}):
  | {
      company: GetCompanyQuery['getCompany'] | null;
      loading: false;
      refetch: () => Promise<GetCompanyQuery['getCompany'] | null>;
      startPolling: (pollInterval: number) => void;
      stopPolling: () => void;
    }
  | {
      company: null;
      loading: true;
      refetch: () => Promise<GetCompanyQuery['getCompany'] | null>;
      startPolling: (pollInterval: number) => void;
      stopPolling: () => void;
    } => {
  const {
    data,
    loading,
    refetch: refetchQuery,
    startPolling,
    stopPolling,
  } = useQuery<GetCompanyQuery, GetCompanyQueryVariables>(
    GET_COMPANY,
    companyId ? { variables: { companyId } } : { skip: !companyId },
  );
  const refetch = async (): Promise<GetCompanyQuery['getCompany'] | null> =>
    companyId ? ((await refetchQuery()).data?.getCompany ?? null) : null;

  // special use case for OrganizationDashboardNav, consider changing this to accept string companyId and removing this guard
  // For example, Cristiano suggested we could use two components in CompanyDashboardNav and switch between then based on companyId
  if (!companyId) {
    return { company: null, loading: false, refetch, startPolling, stopPolling };
  }
  if (loading) {
    return { company: null, loading: true, refetch, startPolling, stopPolling };
  }
  if (!data) {
    throw createError('MissingCompanyError', 'GetCompanyQuery finished loading but no data was returned');
  }
  const company = data.getCompany;
  return { company, loading, refetch, startPolling, stopPolling };
};
